"use client";

import type { NewsLogo } from "./news-types";

import BloombergLogo from "@homeinstead/icons/BloombergLogo";
import FastCompanyLogo from "@homeinstead/icons/FastCompanyLogo";
import ForbesLogo from "@homeinstead/icons/ForbesLogo";
import NewsWeek from "@homeinstead/icons/NewsWeek";
import ParadeLogo from "@homeinstead/icons/ParadeLogo";
import PeopleLogo from "@homeinstead/icons/PeopleLogo";
import TheHuffingtonPostLogo from "@homeinstead/icons/TheHuffingtonPostLogo";
import TimeMagazine from "@homeinstead/icons/TimeMagazine";
import USNews from "@homeinstead/icons/USNews";
import WSJLogo from "@homeinstead/icons/WSJLogo";
import { motion } from "motion/react";
import { useRef } from "react";
import { useResizeObserver } from "usehooks-ts";
import { cn } from "../../utils/classnames";
import { FeaturedNewsLogo } from "./FeaturedNewsLogo";
import { FeaturedOnRow, FeaturedOnStack } from "./FeaturedOn";

// News logo data
const newsLogos: NewsLogo[] = [
  {
    className: "object-left",
    Icon: ForbesLogo,
    title: "Forbes",
    width: "w-fit",
  },
  {
    className: "object-left",
    Icon: WSJLogo,
    title: "Wall Street Journal",
    width: "w-fit",
  },
  {
    className: "object-left",
    Icon: NewsWeek,
    title: "News Week",
    width: "w-fit",
  },
  { className: "object-left", Icon: USNews, title: "US News", width: "w-fit" },
  {
    className: "object-left",
    Icon: PeopleLogo,
    title: "People",
    width: "w-fit",
  },
  {
    className: "object-left",
    Icon: BloombergLogo,
    title: "Bloomberg",
    width: "w-fit",
  },
  {
    className: "object-left",
    Icon: ParadeLogo,
    title: "Parade",
    width: "w-fit",
  },
  {
    className: "object-left",
    Icon: FastCompanyLogo,
    title: "Fast Company",
    width: "w-fit",
  },
  {
    className: "object-left",
    Icon: TheHuffingtonPostLogo,
    title: "The Huffington Post",
    width: "w-fit",
  },
  {
    className: "object-left",
    Icon: TimeMagazine,
    title: "Time",
    width: "w-fit",
  },
];

export const FeaturedNewsLogos = ({
  label,
  animateAlways = true,
  className,
}: {
  label: string;
  animateAlways?: boolean;
  className?: string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const logoSetRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth = 0 } = useResizeObserver({
    ref: containerRef as React.RefObject<HTMLElement>,
  });
  const { width: logoRowWidth = 0 } = useResizeObserver({
    ref: logoSetRef as React.RefObject<HTMLElement>,
  });
  const shouldAnimate = animateAlways || logoRowWidth > containerWidth;
  const animationWidth = logoRowWidth;

  return (
    <div className={cn("w-full", className)}>
      <div className="mx-auto w-full overflow-hidden" ref={containerRef}>
        {label && <FeaturedOnStack label={label} />}
        <div className="mx-auto flex w-full flex-col">
          <div className="w-full lg:flex lg:items-center lg:gap-4">
            {label && <FeaturedOnRow label={label} />}
            <div className="overflow-hidden">
              <motion.div
                animate={shouldAnimate ? { x: [0, -animationWidth - 80] } : {}}
                // gap-20 = 5rem = 80px: offset must match the gap between the two duplicated sets
                className="flex h-full items-center gap-20"
                transition={
                  shouldAnimate
                    ? {
                        x: {
                          duration: 30,
                          ease: "linear",
                          repeat: Infinity,
                          repeatType: "loop",
                        },
                      }
                    : {}
                }
              >
                {/* Measurable first row */}
                <div
                  className="flex shrink-0 items-center gap-20"
                  ref={logoSetRef}
                >
                  {newsLogos.map((logo, index) => (
                    <FeaturedNewsLogo key={`logo-${index}`} {...logo} />
                  ))}
                </div>

                {/* Duplicated row for seamless loop */}
                <div
                  aria-hidden="true"
                  className="flex shrink-0 items-center gap-20"
                >
                  {newsLogos.map((logo, index) => (
                    <FeaturedNewsLogo key={`logo-dup-${index}`} {...logo} />
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
